import { Injectable, inject } from "@angular/core";
import { HttpContextToken, HttpRequest } from "@angular/common/http";
import { MatDialog } from "@angular/material/dialog";
import createDebug from "debug";
import { ErrorDialogComponent } from "@components/error-dialog/error-dialog.component";

const Debug = createDebug("ErrorDialogService");

/**
 * Error Dialog
 *
 * Client-side surface for the generic error dialog. The error-dialog
 * interceptor hands failed writes here; the service shows the server's
 * message in the ErrorDialog component, appending to the open dialog
 * rather than stacking new ones, and exposes the HttpContext token that
 * opts a request out of the generic surface for pages that handle
 * errors themselves.
 */
@Injectable({ providedIn: "root" })
export class ErrorDialogService {
  /** Exclude - HttpContext token that opts a request out of the generic error dialog. Set it on requests whose errors the page handles itself */
  exclude: HttpContextToken<boolean> = new HttpContextToken<boolean>(
    () => false,
  );

  private matDialog = inject(MatDialog);

  /** Dialog Ref - The open error dialog, or undefined when none is open */
  dialogRef?: any;

  /**
   * Handle Http Error - Show a failed write's server message in the error
   * dialog. Skips reads (GET/HEAD/OPTIONS), excluded requests, 401s (the
   * auth flow owns those), and non-HTTP errors. Errors are checked
   * structurally rather than via instanceof so callers can hand in any
   * rejection the HTTP pipeline produces.
   */
  handleHttpError(req: HttpRequest<unknown>, error: unknown): void {
    const debug = Debug.extend("handleHttpError");
    /**
     * Leave reads alone
     *
     * A page that reads and gets nothing back shows its own empty state; only a
     * write the user asked for deserves a dialog in their way.
     */
    if (
      req.method === "GET" ||
      req.method === "HEAD" ||
      req.method === "OPTIONS"
    )
      return;

    // Stand aside for a request that opted out
    if (req.context.get(this.exclude)) return;

    /**
     * Ignore anything that is not an HTTP failure
     *
     * Checked structurally rather than with instanceof so callers can hand in
     * any rejection the HTTP pipeline produces.
     */
    const httpError = error as {
      status?: unknown;
      error?: unknown;
      message?: unknown;
    } | null;
    if (!httpError || typeof httpError.status !== "number") return;

    // Let the auth flow own an unauthenticated response
    if (httpError.status === 401) return;

    /**
     * Prefer the server's message, then the transport's
     *
     * The server's error string is the one written for a person to read; the
     * transport message covers a connection failure that never reached it.
     */
    const body = httpError.error as { error?: unknown } | null;
    const message =
      body && typeof body.error === "string" && body.error
        ? body.error
        : typeof httpError.message === "string" && httpError.message
          ? httpError.message
          : "Request failed";

    // Surface the message in the dialog
    debug(
      "handling http error for %s %j: %j",
      req.method,
      httpError.status,
      message,
    );
    this.showMessage(message);
  }

  /** Show Message - Open the error dialog with the message, or append it when the dialog is already open */
  showMessage(message: string): void {
    const debug = Debug.extend("showMessage");
    /**
     * If a dialog is already open
     *
     * A second dialog would cover the first, so a burst of failed writes reads
     * as one list instead of a stack the user has to dismiss one at a time.
     */
    if (this.dialogRef) {
      // Replace the array so the open dialog renders the added message
      debug("appending message to open dialog: %j", message);
      const instance = this.dialogRef.componentInstance;
      instance.messages = [...instance.messages, message];
      return;
    }

    // Open the dialog with this message as its only entry
    debug("opening error dialog with message: %j", message);
    this.dialogRef = this.matDialog.open(ErrorDialogComponent.contentComponent);
    this.dialogRef.componentInstance.messages = [message];

    // Forget the reference on close so the next message opens a fresh dialog
    this.dialogRef.afterClosed().subscribe(() => {
      this.dialogRef = undefined;
    });
  }
}
